import { parse } from 'papaparse';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import getDataApi from '../api/get-data';
import { adopt } from '@svgdotjs/svg.js';

dayjs.extend(utc);

const asyncPapa = async csvData =>
  new Promise((resolve, reject) => {
    parse(csvData, {
      header: true,
      fastMode: true,
      skipEmptyLines: true,
      transform: (value, header) => {
        if (header === 'timestamp') {
          return Number(value);
        }
        if (header === 'count') {
          return Number(value);
        }
        return value;
      },
      complete: (result, file) => {
        resolve(result.data);
      },
      error: (err, file) => {
        reject(err);
      }
    });
  });

const getUNIXTimestampSeconds = (now = dayjs()) => {
  return dayjs(now).unix();
};

export default {
  async initialFetchAttackData({ commit, state }) {
    commit('modifyAttackDataLoading', true);
    commit('modifyTopIPLoading', true);

    // get the 2 data file and stats
    const [
      { data: prevCSV },
      { data: currentCSV },
      { data: topIPCSV },
      { data: todaySumCSV }
    ] = await Promise.all([
      getDataApi.get('anonym_previous.csv'),
      getDataApi.get('anonym_current.csv'),
      getDataApi.get('top-geo.csv'),
      getDataApi.get('today-sum.csv')
    ]);

    const [prev, current, topIP, todaySum] = await Promise.all([
      asyncPapa(prevCSV),
      asyncPapa(currentCSV),
      asyncPapa(topIPCSV),
      asyncPapa(todaySumCSV)
    ]);

    commit(
      'addTopIP',
      topIP.map(item => {
        return { ...item, count: Number(item.count) };
      })
    );
    commit('modifyTopIPLoading', false);

    const { timestamp } = state;

    const currentTimeInSeconds = getUNIXTimestampSeconds(timestamp);
    const unShiftedAttackData = [...prev, ...current];

    // get the closes index (from below) to the timestamp
    // within 5 minutes, that is highly unlikely that we don't find
    // one
    const indexOfClosesAttackToCurrentTime = unShiftedAttackData.findIndex(
      o => {
        const diff = o.timestamp - currentTimeInSeconds;
        return diff > -5;
      }
    );
    if (indexOfClosesAttackToCurrentTime === -1) {
      // we have trouble, a future error message modal mb?!
    }

    let attackSum = Number(todaySum[0].count);
    for (let i = 0; i < indexOfClosesAttackToCurrentTime; i++) {
      attackSum += Number(unShiftedAttackData[i].count);
    }

    commit('setTotalAttacks', attackSum);

    const shiftedAttackData = unShiftedAttackData.slice(
      indexOfClosesAttackToCurrentTime,
      unShiftedAttackData.length
    );

    commit('addAttackData', shiftedAttackData);

    commit('modifyAttackDataLoading', false);
  },
  async fetchNewAttackData({ commit, state }) {
    // timed fetching for new data

    // if current time is over xx:30 we don't fetch new data
    const nowMinutes = dayjs().minute();
    if (nowMinutes > 30) {
      return;
    }

    const [{ data: currentCSV }, { data: topIPCSV }] = await Promise.all([
      getDataApi.get('anonym_current.csv'),
      getDataApi.get('top-geo.csv')
    ]).catch(err => {
      throw err;
    });

    const [current, topIP] = await Promise.all([
      asyncPapa(currentCSV),
      asyncPapa(topIPCSV)
    ]);

    const { attackData } = state;
    const attackDataLen = attackData.length;
    if (
      attackData[attackDataLen - 1].timestamp ===
      current[current.length - 1].timestamp
    ) {
      // no new data
      return;
    }

    // at this point there is new data so we refresh the top ip and than
    // merge the two attack data set and restart the start and endindex
    commit('addTopIP', topIP);
    commit('addAttackData', [...attackData, ...current]);
  },
  refreshAttackDataCache({ commit, state }) {
    const { attackData } = state;

    const nowPlusOneMin = dayjs().add(1, 'minute');
    const timestamp = getUNIXTimestampSeconds(nowPlusOneMin);
    //  a safety buffer of plus 2 seconds
    const index = attackData.findIndex(o => o.timestamp - timestamp >= 2);

    if (index <= 0) {
      return;
    }

    const attacksToCache = attackData.slice(0, index);
    const remainingAttacks = attackData.slice(index);

    commit('setAttackDataCache', attacksToCache);
    commit('modifyAttackData', remainingAttacks);
  },
  refreshCurrentAttacks({ commit, dispatch, state }) {
    const {
      attackData,
      topIP,
      totalAttacks,
      lastFiveAttack,
      attackDataCache
    } = state;
    const timestamp = dayjs().utcOffset(120);
    commit('modifyTimestamp', timestamp);

    const currentTimeInSeconds = getUNIXTimestampSeconds(timestamp);

    // check if the current time is 55 seconds
    // OR if the chache has no data in it
    const nowSecond = timestamp.second();
    const cacheCurrentLen = attackDataCache.length;

    if (cacheCurrentLen === 0 || nowSecond === 55) {
      dispatch('refreshAttackDataCache');
    }

    // find the first attack with the same timestamp as the
    // current time
    const startIndex = attackDataCache.findIndex(o => {
      const diff = o.timestamp - currentTimeInSeconds;
      return diff === 0;
    });
    // no attack in the current timestamp
    if (startIndex === -1) {
      return;
    }

    // if dispatched mutation modifies the cache we need
    // the new refference of the array, not the old
    let attacks = [...state.attackDataCache];
    const newTopIP = [...topIP];
    const newLastFiveAttack = [...lastFiveAttack];
    let newAttackSum = totalAttacks;
    const topIPCountries = newTopIP.map(o => o.geocountry);

    function modifyTopIPAndSumData(array) {
      array.forEach(attack => {
        const lastFiveCountryCodes = newLastFiveAttack.map(o => o.geocountry);
        const { count, geocountry } = attack;
        const countNum = Number(count);
        // add the count to the big sum
        newAttackSum += countNum;

        // if top countries includes the geocode we add the cont to it
        if (topIPCountries.includes(geocountry)) {
          const index = topIPCountries.indexOf(geocountry);

          newTopIP[index].count += countNum;
        }

        // also we refresh the last five attack array
        if (lastFiveCountryCodes.includes(geocountry)) {
          const index = newLastFiveAttack.findIndex(
            o => o.geocountry === geocountry
          );
          newLastFiveAttack[index].count += count;
          const newObj = { ...newLastFiveAttack[index] };
          newLastFiveAttack.splice(index, 1);
          newLastFiveAttack.unshift(newObj);
        } else {
          newLastFiveAttack.unshift(attack);
        }
      });
    }

    // there are attacks that are not processed before
    if (startIndex > 0) {
      const previousAttacks = attackData.slice(0, startIndex);

      modifyTopIPAndSumData(previousAttacks);

      attacks = attacks.slice(startIndex);
    }

    const attacksLen = attacks.length;
    let endIndex = attacks.findIndex(o => {
      const diff = o.timestamp - currentTimeInSeconds;
      return diff !== 0;
    });

    // we subtracted 1 of the index, becouse we wan't to get
    // the index of the last item that is equal of the the
    // current timestamp
    if (endIndex === -1) {
      // no more data in the array
      endIndex = attacksLen;
    }

    const currentAttacks = attacks.slice(0, endIndex);
    const remainingAttacks = attacks.slice(endIndex);

    commit('changeCurrentAttacks', currentAttacks);
    commit('refreshAttackDataCache', remainingAttacks);

    // also we modify the topIP data
    modifyTopIPAndSumData(currentAttacks);
    commit('setTotalAttacks', newAttackSum);
    commit('addTopIP', newTopIP);
    // and the last 5 attack array
    commit('refreshLastFiveAttack', newLastFiveAttack);
  },
  async fetchTopIP({ commit }) {
    commit('modifyTopIPLoading', true);
    // let data;

    commit('modifyAttackDataLoading', true);

    const { data: downloadedCSV } = await getDataApi
      .get('top-geo.csv')
      .catch(err => {
        throw err;
      });
    const data = await asyncPapa(downloadedCSV);

    commit('addTopIP', data);

    commit('modifyTopIPLoading', false);
  },
  adoptCountriesSVG({ commit, state }) {
    const svgArray = state.countryPathsArray.map(path => {
      const el = document.getElementById(path.id);
      return { id: path.id, svg: adopt(el) };
    });
    commit('addSVGCountries', svgArray);
  }
};
